import {useState, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";

const useDataPanel = ({featuresState, layersState}) => {
    const {t} = useTranslation(["webgis"]);
    const {stationSelectedState, model23SelectedState, model1SelectedState, incidentSelectedState} = featuresState;
    const {layers} = layersState;

    const [isTablesActive, setIsTablesActive] = useState(false);
    const [activeContentType, setActiveContentType] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const tableTypes = useMemo(() => [
        {id: 'stationTable', layerId: 'stations', label: t("layers.stations"), genericLabel: t("layers.stations")},
        {id: 'model1Table', layerId: 'model1Stations', label: t("layers.model1Stations"), genericLabel: t("layers.model1Stations")},
        {id: 'model23Table', layerId: 'models23', label: t("layers.models23"), genericLabel: t("layers.models23")},
        {id: 'incidentTable', layerId: 'incidents', label: t("layers.incidents"), genericLabel: t("layers.incidents")},
    ], [t]);

    const activeTableTypes = useMemo(() => tableTypes.filter(tt => layers[tt.layerId].active), [tableTypes, layers]);

    const availableContents = useMemo(() => {
        const contents = [];

        // Only show one table entry: the active one or the most relevant one
        let tableToShowId = activeTableTypes.length > 0 ? activeTableTypes[0].id : null;
        if (activeContentType && activeContentType.endsWith('Table')) {
            tableToShowId = activeContentType;
        }
        if (activeContentType === 'station'){
            tableToShowId = 'stationTable';
        }
        else if (activeContentType === 'model23') {
            tableToShowId = 'model23Table';
        }
        else if (activeContentType === 'model1') {
            tableToShowId = 'model1Table';
        }
        else if (activeContentType === 'incident') {
            tableToShowId = 'incidentTable';
        }

        if (tableToShowId) {
            const tt = tableTypes.find(t => t.id === tableToShowId);
            if (tt && layers[tt.layerId].active) {
                contents.push({
                    type: tt.id,
                    label: t('tablesTitle'),
                    genericLabel: t('tablesTitle'),
                });
            }
        }

        if (stationSelectedState.selectedFeatures.length > 0) {
            let label;
            let genericLabel = t("layers.stations");
            if (stationSelectedState.selectedFeatures.length === 1) {
                label = `${t("layers.stations")} (${stationSelectedState.selectedFeatures[0].properties.code})`;
            } else {
                label = `${t("layers.stations")} (${t("selectedElements", { count: stationSelectedState.selectedFeatures.length })})`;
            }

            contents.push({
                type: 'station',
                label,
                genericLabel
            });
        }
        if (model23SelectedState.selectedFeatures.length > 0) {
            let label;
            let genericLabel = t("layers.models23");
            if (model23SelectedState.selectedFeatures.length === 1) {
                label = `${t("layers.models23")} (${model23SelectedState.selectedFeatures[0].properties.station?.code || model23SelectedState.selectedFeatures[0].properties.id})`;
            } else {
                label = `${t("layers.models23")} (${t("selectedElements", { count: model23SelectedState.selectedFeatures.length })})`;
            }
            contents.push({
                type: 'model23',
                label,
                genericLabel
            });
        }
        if (model1SelectedState.selectedFeatures.length > 0) {
            let label;
            let genericLabel = t("layers.model1Stations");
            if (model1SelectedState.selectedFeatures.length === 1) {
                label = `${t("layers.model1Stations")} (${model1SelectedState.selectedFeatures[0].properties.code})`;
            } else {
                label = `${t("layers.model1Stations")} (${t("selectedElements", { count: model1SelectedState.selectedFeatures.length })})`;
            }
            contents.push({
                type: 'model1',
                label,
                genericLabel
            });
        }
        if (incidentSelectedState.selectedFeatures.length > 0) {
            let label;
            let genericLabel = t("layers.incidents");
            if (incidentSelectedState.selectedFeatures.length === 1) {
                label = `${t("layers.incidents")} (${incidentSelectedState.selectedFeatures[0].properties.locationName})`;
            } else {
                label = `${t("layers.incidents")} (${t("selectedElements", { count: incidentSelectedState.selectedFeatures.length })})`;
            }
            contents.push({
                type: 'incident',
                label,
                genericLabel
            });
        }
        return contents;
    }, [isTablesActive, layers, tableTypes, stationSelectedState.selectedFeatures, model23SelectedState.selectedFeatures, t, activeContentType, model1SelectedState.selectedFeatures, incidentSelectedState.selectedFeatures]);

    // If active content is removed from available contents, switch to another one or close
    useEffect(() => {
        if (isOpen && activeContentType) {
            const isActiveAvailable = availableContents.some(c => c.type === activeContentType);
            if (!isActiveAvailable) {
                if (availableContents.length > 0) {
                    setActiveContentType(availableContents[0].type);
                } else {
                    setIsOpen(false);
                    setActiveContentType(null);
                }
            }
        }
    }, [availableContents, isOpen, activeContentType]);

    const close = () => {
        setIsOpen(false);
        setIsTablesActive(false);
        stationSelectedState.clearSelection();
        model23SelectedState.clearSelection();
        model1SelectedState.clearSelection();
        incidentSelectedState.clearSelection();
        setActiveContentType(null);
    };

    const open = () => {
        setIsOpen(true);
    }

    const toggleTables = () => {
        if (!isOpen) {
            activeTableTypes.length > 0 && setActiveContentType(activeTableTypes[0].id);
            setIsOpen(true);
        } else {
            close();
        }
    }

    return {
        isOpen,
        activeContentType,
        availableContents,
        setActiveContentType,
        toggleTables,
        close,
        open
    };
};

export default useDataPanel;
